import { useEffect } from "react";

import Loader from "components/Loader";

import { queryGetEmployeeSetting } from "@type/api/user";
import { INIT_USER } from "@type/store/AuthStore";
import { queryGetUser } from "api/user/getUser";
import { useTranslationContext } from "context/TranslationContext";
import { clearCookies, getCookies } from "lib/cookies";
import { useQueryWithCallbacks } from "lib/query/useQueryWithCallbacks";
import { resetAllStores } from "lib/store";
import { useAuthStore } from "store";

import { LanguageType } from "types/index";
import { AUTH_COOKIES_KEY, EACOUNT_TYPE } from "constants/index";

interface AuthProps {
  children: React.ReactNode;
}

const AuthDataWrapper = ({ children }: AuthProps) => {
  const isLoadingAuth = useAuthStore((state) => state.isLoading);
  const user = useAuthStore((state) => state.user);
  const { changeLanguage } = useTranslationContext();

  const onSetIsLogin = useAuthStore((state) => state.onSetIsLogin);
  const onSetUser = useAuthStore((state) => state.onSetUser);
  const onSetLoadingAuth = useAuthStore((state) => state.onSetLoading);

  useQueryWithCallbacks({
    queryKey: queryGetEmployeeSetting._key(user.idUnique),
    queryFn: () => queryGetEmployeeSetting(user.idUnique),
    enabled: !!user?.idUnique,
    onSuccess: (data) => {
      if (data.setting.language) {
        onSetUser({
          ...user,
          language: data.setting.language,
        });
        changeLanguage(data.setting.language as LanguageType);
      }
      onSetLoadingAuth(false);
      onSetIsLogin(true);
    },
    onError: () => {
      onSetIsLogin(true);
      onSetLoadingAuth(false);
    },
  });

  const { isLoading: isLoadingGetUser } = useQueryWithCallbacks({
    queryKey: queryGetUser._key(),
    queryFn: () => queryGetUser(),
    enabled: !!getCookies(AUTH_COOKIES_KEY.TOKEN),
    onSuccess: (data) => {
      if (data.idUnique) {
        onSetUser({
          ...user,
          ...data,
          isSuperAdmin: data.type === EACOUNT_TYPE.SUPERADMIN,
        });
      } else {
        resetAllStores();
        onSetLoadingAuth(false);
        window.location.reload();
      }
    },
    onError: () => {
      onSetUser(INIT_USER);
      onSetIsLogin(false);
      clearCookies();
    },
  });

  useEffect(() => {
    if (isLoadingGetUser) {
      onSetLoadingAuth(isLoadingGetUser);
    }
  }, [isLoadingGetUser, onSetLoadingAuth]);

  if (isLoadingAuth) {
    return <Loader />;
  }

  return <>{children}</>;
};

export default AuthDataWrapper;
